<template>
  <!--
    AI 每日签到 + 积分余额（所有 AI 对话页共用）

    设计约束（主人 2026-10-28）：「既显眼又不遮挡功能」。
    因此不做浮层、不做弹窗，只占一行内一个 chip 的位置：
      · 未签到 → 高饱和金色 + 外发光，视线自然被吸过去；
      · 已签到 → 降为低对比度（描边 + 半透明），当天不再干扰阅读。
    余额与签到按钮同一行，避免再开一行占掉消息区高度。
  -->
  <div v-if="ready" class="ai-signin" :class="[`variant-${variant}`, { 'is-signed': signedToday }]">
    <span class="ai-signin-balance" title="AI 积分余额：每次问答按 token 扣减，签到可补充">
      积分 {{ credits }}
    </span>
    <button
      class="ai-signin-btn"
      :disabled="signedToday || loading"
      :title="signedToday ? '今天已签到，明天再来' : `每日签到可领 ${signinAmount} 积分`"
      @click="handleSignin"
    >
      <svg
        v-if="!signedToday"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.4"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <rect x="3" y="4" width="18" height="17" rx="3" />
        <line x1="8" y1="2" x2="8" y2="6" />
        <line x1="16" y1="2" x2="16" y2="6" />
        <polyline points="8 13 11 16 16 10" />
      </svg>
      <svg
        v-else
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.6"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="20 6 9 17 4 12" />
      </svg>
      <span>{{ btnText }}</span>
    </button>
  </div>
</template>

<script setup>
/**
 * 每日签到控件。
 *
 * 发放逻辑完全在 pay 侧（{@code pay_token_signin_record} 以
 * 用户 + 代币 + 部门 + 日期 为唯一键），后台对话页、悬浮助手、官网浮窗共用同一份记录：
 * 任一入口签到过，其它入口当天都会显示「已签到」——签到跟人不跟页面。
 *
 * 失败静默：接口异常不弹全局错误、不占位——签到是增值入口，
 * 不能因为它挂了影响正常提问。
 */
import { computed, onMounted, ref } from 'vue'
import { ElMessage } from 'element-plus'
import {
  getCreditAccount,
  getSigninStatus,
  signinCredit
} from '@/modules/huyu-framework/backend/api/huyuAI/aiCredit'

const props = defineProps({
  /** chip：输入条工具区（浅底）；header：深色渐变头部 */
  variant: { type: String, default: 'chip' },
  tokenCode: { type: String, default: 'ai_token' }
})

const credits = ref(0)
const signedToday = ref(false)
const signinAmount = ref(0)
const signinEnabled = ref(true)
const loading = ref(false)
const ready = ref(false)

const btnText = computed(() => {
  if (loading.value) return '签到中…'
  if (signedToday.value) return '已签到'
  return signinAmount.value > 0 ? `签到 +${signinAmount.value}` : '签到'
})

/** 账户余额：AI 代币四类余额相加（与后端 BalanceCheckStage 口径一致） */
function totalBalance(account) {
  if (!account) return 0
  return (
    Number(account.paidBalance || 0) +
    Number(account.giftBalance || 0) +
    Number(account.activityBalance || 0) +
    Number(account.subsidyBalance || 0)
  )
}

async function refresh() {
  const [accountRes, statusRes] = await Promise.allSettled([
    getCreditAccount(props.tokenCode),
    getSigninStatus(props.tokenCode)
  ])

  if (accountRes.status === 'fulfilled' && accountRes.value && accountRes.value.code === 200) {
    credits.value = totalBalance(accountRes.value.data)
  } else {
    // 账户尚未开通（新用户）：显示 0，签到时会由 pay 侧自动开户
    credits.value = 0
  }

  if (statusRes.status === 'fulfilled' && statusRes.value && statusRes.value.code === 200) {
    const d = statusRes.value.data || {}
    signedToday.value = !!d.signedToday
    signinEnabled.value = d.signinEnabled !== false
    signinAmount.value = Number(d.signinAmount || 0)
  }
  ready.value = true
}

async function handleSignin() {
  if (loading.value || signedToday.value) return
  loading.value = true
  try {
    const res = await signinCredit(props.tokenCode)
    const granted = Number(res?.data?.grantAmount || signinAmount.value || 0)
    ElMessage.success(granted > 0 ? `签到成功，+${granted} 积分` : '签到成功')
    await refresh()
  } catch (e) {
    const msg = e?.message || e?.response?.data?.msg || '签到失败，请稍后重试'
    // 「今日已签到」不是错误，按成功状态回显即可
    if (/已签到/.test(msg)) {
      ElMessage.info('今天已经签过了，明天再来')
    } else {
      ElMessage.warning(msg)
    }
    await refresh()
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  void refresh()
})

defineExpose({ refresh })
</script>

<style scoped>
.ai-signin {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: 0 0 auto;
  white-space: nowrap;
  font-size: 12px;
  line-height: 1;
}

.ai-signin-balance {
  opacity: 0.75;
  font-variant-numeric: tabular-nums;
}

.ai-signin-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 9px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
  border: 1px solid #f59e0b;
  background: #fbbf24;
  color: #78350f;
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.22);
}

.ai-signin-btn:hover:not(:disabled) {
  background: #f59e0b;
  box-shadow: 0 0 0 4px rgba(251, 191, 36, 0.28);
}

.ai-signin.is-signed .ai-signin-btn {
  cursor: default;
  font-weight: 400;
  background: transparent;
  color: var(--el-text-color-secondary, #909399);
  border-color: var(--el-border-color, #dcdfe6);
  box-shadow: none;
}

/* 深色头部（悬浮助手）：未签到用金色实心，已签到用半透明白描边 */
.variant-header .ai-signin-balance {
  color: rgba(255, 255, 255, 0.85);
  opacity: 1;
}

.variant-header .ai-signin-btn {
  border-color: #f59e0b;
  background: #fbbf24;
  color: #78350f;
  box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.25);
}

.variant-header.is-signed .ai-signin-btn {
  background: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.78);
  border-color: rgba(255, 255, 255, 0.28);
  box-shadow: none;
}

/* 窄屏（消息区更窄）时收紧文案，避免挤压输入框 */
@media (max-width: 768px) {
  .ai-signin-balance {
    display: none;
  }
}
</style>
